import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  createAccountingRule,
  deleteAccountingRule,
  getAccountingRules,
  updateAccountingRule,
} from '../../../services/treasuryAPI';

export const RULE_CATEGORIES = [
  'Compras',
  'Producción',
  'Ventas',
  'Tesorería',
  'Nómina',
  'Impuestos',
  'Activos Fijos',
  'Cierre',
];

export const RULE_TRIGGERS = [
  { value: 'purchase.in_transit', label: 'Compra: MP en tránsito', category: 'Compras' },
  { value: 'purchase.received', label: 'Compra: recepción de MP', category: 'Compras' },
  { value: 'purchase.invoice', label: 'Compra: factura proveedor', category: 'Compras' },
  { value: 'production.issued', label: 'Producción: consumo de MP', category: 'Producción' },
  { value: 'production.manufactured', label: 'Producción: producto fabricado', category: 'Producción' },
  { value: 'production.finished', label: 'Producción: producto terminado', category: 'Producción' },
  { value: 'delivery.dispatched', label: 'Despacho a tránsito', category: 'Ventas' },
  { value: 'delivery.received', label: 'Recepción de producto', category: 'Ventas' },
  { value: 'sale.billed', label: 'Emisión de factura', category: 'Ventas' },
  { value: 'sale.non_operating', label: 'Venta exenta / ingreso no operacional', category: 'Ventas' },
  { value: 'payment.received', label: 'Pago recibido de cliente', category: 'Tesorería' },
  { value: 'sale.credit_note', label: 'Nota de crédito de venta', category: 'Ventas' },
  { value: 'treasury.income', label: 'Ingreso de tesorería', category: 'Tesorería' },
  { value: 'treasury.expense', label: 'Egreso de tesorería', category: 'Tesorería' },
  { value: 'payment.issued', label: 'Pago emitido a proveedor', category: 'Tesorería' },
  { value: 'payroll.accrual', label: 'Provisión / pago de nómina', category: 'Nómina' },
  { value: 'tax.monthly', label: 'Declaración / pago de impuestos', category: 'Impuestos' },
  { value: 'asset.depreciation', label: 'Depreciación de activos', category: 'Activos Fijos' },
  { value: 'close.monthly', label: 'Cierre mensual', category: 'Cierre' },
];

export const DOCUMENT_TYPES = [
  'Factura',
  'Factura electrónica',
  'Factura no afecta o exenta electrónica',
  'Boleta electrónica',
  'Boleta exenta electrónica',
  'Nota de Crédito',
  'Nota de Débito',
  'Transferencia',
  'Cheque',
  'Recibo',
  'Guía de despacho',
  'Orden de producción',
  'Orden de compra',
  'Pago',
  'Cierre',
  'Otro',
];

export const AMOUNT_BASES = [
  { value: 'total', label: 'Total' },
  { value: 'net', label: 'Neto' },
  { value: 'tax', label: 'IVA / Impuesto' },
  { value: 'exempt', label: 'Exento' },
  { value: 'cost', label: 'Costo' },
];

export const RULE_STATUSES = [
  { value: 'Borrador', label: 'Borrador', color: '#a16207', bg: '#fef3c7', darkBg: '#78350f' },
  { value: 'Activa', label: 'Activa', color: '#047857', bg: '#d1fae5', darkBg: '#065f46' },
  { value: 'Inactiva', label: 'Inactiva', color: '#b91c1c', bg: '#fee2e2', darkBg: '#7f1d1d' },
];

const statusMeta = (status) =>
  RULE_STATUSES.find((item) => item.value === status) || RULE_STATUSES[0];

const emptyLine = (side = 'debe') => ({
  side,
  line_order: side === 'debe' ? 10 : 20,
  chart_account_id: '',
  detail_node_id: '',
  amount_basis: 'total',
  percentage: 100,
});

const emptyForm = () => ({
  name: '',
  category: 'Compras',
  document_type: 'Orden de compra',
  trigger_event: 'purchase.in_transit',
  status: 'Borrador',
  description: '',
  billing_document_type: '',
  payment_terms: '',
  entry_type: '',
  reference_document_type: '',
  priority: 100,
  lines: [emptyLine('debe'), emptyLine('haber')],
});

const chartLeafLabel = (row) => {
  for (const key of [
    'detail_level_6',
    'detail_level_5',
    'detail_level_4',
    'detail_level_3',
    'detail_level_2',
    'detail_level_1',
    'subaccount',
    'account',
    'category',
  ]) {
    const value = String(row?.[key] || '').trim();
    if (value) return value;
  }
  return row?.account_code || 'Cuenta';
};

const triggerLabel = (value) =>
  RULE_TRIGGERS.find((item) => item.value === value)?.label || value;

const FinanceAccountingRulesPanel = ({ ui, chartAccounts = [], isDarkMode = false }) => {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const payload = await getAccountingRules();
      setRules(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las reglas contables.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const accountOptions = useMemo(
    () =>
      (chartAccounts || []).map((row) => ({
        value: String(row.id),
        label: `${row.account_code || 'COA'} · ${chartLeafLabel(row)}`,
      })),
    [chartAccounts]
  );

  const filteredRules = useMemo(() => {
    return rules.filter((rule) => {
      if (categoryFilter !== 'all' && rule.category !== categoryFilter) return false;
      if (statusFilter !== 'all' && rule.status !== statusFilter) return false;
      return true;
    });
  }, [rules, categoryFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { Activa: 0, Borrador: 0, Inactiva: 0 };
    rules.forEach((rule) => {
      const key = rule.status || 'Borrador';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [rules]);

  const groupedRules = useMemo(() => {
    const map = new Map();
    filteredRules.forEach((rule) => {
      const key = rule.category || 'Sin categoría';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(rule);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredRules]);

  const panelStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: 12,
    background: ui.panel,
  };

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    padding: '0 10px',
    fontSize: 13,
    boxSizing: 'border-box',
    width: '100%',
  };

  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive,
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const ghostBtn = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name || '',
      category: rule.category || 'Ventas',
      document_type: rule.document_type || '',
      trigger_event: rule.trigger_event || 'purchase.in_transit',
      status: rule.status || 'Borrador',
      description: rule.description || '',
      billing_document_type: rule.billing_document_type || '',
      payment_terms: rule.payment_terms || '',
      entry_type: rule.entry_type || '',
      reference_document_type: rule.reference_document_type || '',
      priority: rule.priority ?? 100,
      lines: (rule.lines || []).length
        ? rule.lines.map((line) => ({
            side: line.side,
            line_order: line.line_order || 10,
            chart_account_id: line.chart_account_id ? String(line.chart_account_id) : '',
            detail_node_id: line.detail_node_id ? String(line.detail_node_id) : '',
            amount_basis: line.amount_basis || 'total',
            percentage: Number(line.percentage ?? 100),
          }))
        : [emptyLine('debe'), emptyLine('haber')],
    });
    setFormError('');
    setShowForm(true);
  };

  const updateLine = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, idx) => (idx === index ? { ...line, ...patch } : line)),
    }));
  };

  const addLine = (side) => {
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, emptyLine(side)],
    }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, idx) => idx !== index),
    }));
  };

  const handleSave = async () => {
    if (!String(form.name || '').trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    if (!form.trigger_event) {
      setFormError('Debes seleccionar un disparador de transacción.');
      return;
    }
    const status = form.status || 'Borrador';
    const lines = (form.lines || []).filter((line) => line.chart_account_id || line.detail_node_id);
    if (status === 'Activa') {
      if (!lines.some((line) => line.side === 'debe') || !lines.some((line) => line.side === 'haber')) {
        setFormError('Para activar la regla incluye al menos una cuenta en DEBE y una en HABER.');
        return;
      }
      if (!accountOptions.length) {
        setFormError('Primero crea cuentas en el Plan de cuentas para poder mapear DEBE/HABER.');
        return;
      }
    }

    const payload = {
      name: form.name.trim(),
      category: form.category,
      document_type: form.document_type || '',
      trigger_event: form.trigger_event,
      status,
      description: form.description || '',
      billing_document_type: form.billing_document_type || '',
      payment_terms: form.payment_terms || '',
      entry_type: form.entry_type || '',
      reference_document_type: form.reference_document_type || '',
      priority: Number(form.priority || 100),
      lines: lines.map((line, idx) => ({
        side: line.side,
        line_order: Number(line.line_order || (idx + 1) * 10),
        chart_account_id: line.chart_account_id ? Number(line.chart_account_id) : null,
        detail_node_id: line.detail_node_id ? Number(line.detail_node_id) : null,
        amount_basis: line.amount_basis || 'total',
        percentage: Number(line.percentage || 100),
      })),
    };

    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) {
        await updateAccountingRule(editingId, payload);
      } else {
        await createAccountingRule(payload);
      }
      setShowForm(false);
      setEditingId(null);
      await loadRules();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar la regla.');
    } finally {
      setIsSaving(false);
    }
  };

  const buildRulePayload = (rule, statusOverride) => ({
    name: rule.name,
    category: rule.category,
    document_type: rule.document_type || '',
    trigger_event: rule.trigger_event,
    status: statusOverride || rule.status || 'Borrador',
    description: rule.description || '',
    billing_document_type: rule.billing_document_type || '',
    payment_terms: rule.payment_terms || '',
    entry_type: rule.entry_type || '',
    reference_document_type: rule.reference_document_type || '',
    priority: Number(rule.priority || 100),
    lines: (rule.lines || []).map((line, idx) => ({
      side: line.side,
      line_order: Number(line.line_order || (idx + 1) * 10),
      chart_account_id: line.chart_account_id ? Number(line.chart_account_id) : null,
      detail_node_id: line.detail_node_id ? Number(line.detail_node_id) : null,
      amount_basis: line.amount_basis || 'total',
      percentage: Number(line.percentage || 100),
    })),
  });

  const handleSetStatus = async (rule, nextStatus) => {
    if (nextStatus === 'Activa') {
      const lines = rule.lines || [];
      const hasDebe = lines.some(
        (line) => line.side === 'debe' && (line.chart_account_id || line.detail_node_id)
      );
      const hasHaber = lines.some(
        (line) => line.side === 'haber' && (line.chart_account_id || line.detail_node_id)
      );
      if (!hasDebe || !hasHaber) {
        window.alert(
          'No se puede activar: la regla necesita al menos una línea DEBE y una HABER con cuenta.'
        );
        return;
      }
    }
    try {
      await updateAccountingRule(rule.id, buildRulePayload(rule, nextStatus));
      await loadRules();
    } catch (err) {
      window.alert(err.message || 'No se pudo cambiar el estado.');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"?`)) return;
    try {
      await deleteAccountingRule(rule.id);
      await loadRules();
    } catch (err) {
      window.alert(err.message || 'No se pudo eliminar la regla.');
    }
  };

  const renderLinesEditor = (side) => {
    const sideLines = form.lines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => line.side === side);
    return (
      <div
        style={{
          border: `1px solid ${side === 'debe' ? (isDarkMode ? '#1f4d99' : '#bfdbfe') : isDarkMode ? '#065f46' : '#a7f3d0'}`,
          borderRadius: 10,
          padding: 10,
          background: side === 'debe' ? (isDarkMode ? '#0b1d3b' : '#eff6ff') : isDarkMode ? '#052e26' : '#ecfdf5',
          display: 'grid',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: side === 'debe' ? '#3b82f6' : '#10b981' }}>
            {side === 'debe' ? 'DEBE' : 'HABER'}
          </p>
          <button type="button" style={{ ...ghostBtn, height: 28, fontSize: 12 }} onClick={() => addLine(side)}>
            <Plus size={12} /> Línea
          </button>
        </div>
        {!sideLines.length ? (
          <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>Sin líneas.</p>
        ) : (
          sideLines.map(({ line, index }) => (
            <div
              key={`${side}-${index}`}
              style={{ display: 'grid', gridTemplateColumns: '1fr 110px 80px 32px', gap: 6, alignItems: 'center' }}
            >
              <select
                value={line.chart_account_id}
                onChange={(e) => updateLine(index, { chart_account_id: e.target.value })}
                style={inputStyle}
              >
                <option value="">Selecciona cuenta</option>
                {accountOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={line.amount_basis}
                onChange={(e) => updateLine(index, { amount_basis: e.target.value })}
                style={inputStyle}
              >
                {AMOUNT_BASES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0.01}
                max={100}
                step={0.01}
                value={line.percentage}
                onChange={(e) => updateLine(index, { percentage: e.target.value })}
                style={inputStyle}
                title="% del monto"
              />
              <button
                type="button"
                onClick={() => removeLine(index)}
                style={{ ...ghostBtn, height: 34, padding: 0, justifyContent: 'center', color: ui.danger, borderColor: ui.danger }}
                title="Quitar línea"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...panelStyle, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} /> Reglas contables
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
            Solo las reglas Activas generan asientos. {statusCounts.Activa || 0} activas ·{' '}
            {statusCounts.Borrador || 0} borrador · {statusCounts.Inactiva || 0} inactivas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ ...inputStyle, width: 150 }}
          >
            <option value="all">Todos los estados</option>
            {RULE_STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ ...inputStyle, width: 180 }}>
            <option value="all">Todas las categorías</option>
            {RULE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <button type="button" style={primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Nueva regla
          </button>
        </div>
      </div>

      {!accountOptions.length ? (
        <p style={{ margin: 0, fontSize: 12, color: ui.warning || ui.muted }}>
          Aún no hay cuentas en el plan de cuentas. Créalas en la pestaña Plan de cuentas antes de mapear asientos.
        </p>
      ) : null}

      {error ? <p style={{ margin: 0, fontSize: 13, color: ui.danger }}>{error}</p> : null}

      {isLoading ? (
        <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>Cargando reglas...</p>
      ) : !filteredRules.length ? (
        <div style={{ ...panelStyle, padding: 20 }}>
          <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>
            {rules.length
              ? 'No hay reglas con esos filtros.'
              : 'No hay reglas todavía. Crea la primera para ventas, tesorería u otros tipos de transacción.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {groupedRules.map(([category, group]) => (
            <div key={category} style={{ ...panelStyle, padding: 12, display: 'grid', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: ui.text }}>
                {category} ({group.length})
              </p>
              {group.map((rule) => {
                const meta = statusMeta(rule.status);
                return (
                <div
                  key={rule.id}
                  style={{
                    border: `1px solid ${ui.border}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: ui.section || ui.header,
                    display: 'grid',
                    gap: 8,
                    opacity: rule.status === 'Inactiva' ? 0.72 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>{rule.name}</span>
                      <span style={{ fontSize: 11, color: ui.muted }}>{rule.rule_code}</span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          borderRadius: 999,
                          padding: '2px 7px',
                          background: isDarkMode ? meta.darkBg : meta.bg,
                          color: isDarkMode ? '#fff' : meta.color,
                          border: `1px solid ${meta.color}55`,
                        }}
                      >
                        {rule.status || 'Borrador'}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          borderRadius: 999,
                          padding: '2px 7px',
                          background: isDarkMode ? '#3f3f46' : '#e5e7eb',
                          color: ui.textSecondary,
                        }}
                      >
                        {triggerLabel(rule.trigger_event)}
                      </span>
                      {rule.document_type ? (
                        <span style={{ fontSize: 11, color: ui.muted }}>{rule.document_type}</span>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {rule.status !== 'Activa' ? (
                        <button
                          type="button"
                          style={{ ...ghostBtn, height: 28, color: '#047857', borderColor: '#047857' }}
                          onClick={() => handleSetStatus(rule, 'Activa')}
                          title="Activar regla (empieza a generar asientos)"
                        >
                          Activar
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={{ ...ghostBtn, height: 28, color: '#a16207', borderColor: '#a16207' }}
                          onClick={() => handleSetStatus(rule, 'Borrador')}
                          title="Pasar a borrador (deja de generar asientos)"
                        >
                          A borrador
                        </button>
                      )}
                      {rule.status !== 'Inactiva' ? (
                        <button
                          type="button"
                          style={{ ...ghostBtn, height: 28 }}
                          onClick={() => handleSetStatus(rule, 'Inactiva')}
                          title="Desactivar regla"
                        >
                          Desactivar
                        </button>
                      ) : null}
                      <button type="button" style={{ ...ghostBtn, height: 28 }} onClick={() => openEdit(rule)}>
                        <Pencil size={13} /> Editar
                      </button>
                      <button
                        type="button"
                        style={{ ...ghostBtn, height: 28, color: ui.danger, borderColor: ui.danger }}
                        onClick={() => handleDelete(rule)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {rule.description ? (
                    <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{rule.description}</p>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {['debe', 'haber'].map((side) => (
                      <div
                        key={side}
                        style={{
                          border: `1px solid ${side === 'debe' ? (isDarkMode ? '#1f4d99' : '#bfdbfe') : isDarkMode ? '#065f46' : '#a7f3d0'}`,
                          borderRadius: 8,
                          padding: '6px 8px',
                          background: side === 'debe' ? (isDarkMode ? '#0b1d3b' : '#eff6ff') : isDarkMode ? '#052e26' : '#ecfdf5',
                        }}
                      >
                        <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: side === 'debe' ? '#3b82f6' : '#10b981' }}>
                          {side === 'debe' ? 'DEBE' : 'HABER'}
                        </p>
                        {(rule.lines || [])
                          .filter((line) => line.side === side)
                          .map((line) => (
                            <p key={line.id} style={{ margin: 0, fontSize: 11, color: ui.text }}>
                              <strong>{line.account_code_snapshot || '—'}</strong> {line.account_label_snapshot}{' '}
                              <span style={{ color: ui.muted }}>
                                ({line.amount_basis}
                                {Number(line.percentage) !== 100 ? ` · ${line.percentage}%` : ''})
                              </span>
                            </p>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              width: 560,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
                {editingId ? 'Editar regla contable' : 'Nueva regla contable'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Asocia un disparador de transacción con su asiento DEBE/HABER. Guarda como borrador
                mientras la defines; solo las activas generan asientos.
              </p>
            </div>

            <div style={{ overflowY: 'auto', display: 'grid', gap: 12, alignContent: 'start' }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Nombre
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  style={inputStyle}
                  placeholder="Ej. Registro venta con IVA"
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Categoría
                  <select
                    value={form.category}
                    onChange={(e) => {
                      const category = e.target.value;
                      const firstTrigger = RULE_TRIGGERS.find((item) => item.category === category);
                      setForm((prev) => ({
                        ...prev,
                        category,
                        trigger_event: firstTrigger?.value || prev.trigger_event,
                      }));
                    }}
                    style={inputStyle}
                  >
                    {RULE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Estado
                  <select
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="Borrador">Borrador</option>
                    <option value="Activa">Activa</option>
                    <option value="Inactiva">Inactiva</option>
                  </select>
                </label>
              </div>

              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Disparador
                <select
                  value={form.trigger_event}
                  onChange={(e) => setForm((prev) => ({ ...prev, trigger_event: e.target.value }))}
                  style={inputStyle}
                >
                  {RULE_TRIGGERS.filter((item) => item.category === form.category).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Tipo de documento
                  <select
                    value={form.document_type}
                    onChange={(e) => setForm((prev) => ({ ...prev, document_type: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">Sin especificar</option>
                    {DOCUMENT_TYPES.map((doc) => (
                      <option key={doc} value={doc}>
                        {doc}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Prioridad
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
              </div>

              {(form.category === 'Tesorería') ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                    Tipo ingreso/egreso
                    <select
                      value={form.entry_type}
                      onChange={(e) => setForm((prev) => ({ ...prev, entry_type: e.target.value }))}
                      style={inputStyle}
                    >
                      <option value="">Cualquiera</option>
                      <option value="income">Ingreso</option>
                      <option value="expense">Egreso</option>
                    </select>
                  </label>
                  <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                    Doc. referencia
                    <select
                      value={form.reference_document_type}
                      onChange={(e) => setForm((prev) => ({ ...prev, reference_document_type: e.target.value }))}
                      style={inputStyle}
                    >
                      <option value="">Cualquiera</option>
                      {DOCUMENT_TYPES.map((doc) => (
                        <option key={doc} value={doc}>
                          {doc}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}

              {(form.category === 'Ventas') ? (
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Tipo documento de cobro
                  <select
                    value={form.billing_document_type}
                    onChange={(e) => setForm((prev) => ({ ...prev, billing_document_type: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">Cualquiera</option>
                    <option value="Factura">Factura</option>
                    <option value="Boleta electrónica">Boleta electrónica</option>
                  </select>
                </label>
              ) : null}

              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Descripción
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical' }}
                  placeholder="Qué hace esta regla y cuándo aplica"
                />
              </label>

              {renderLinesEditor('debe')}
              {renderLinesEditor('haber')}

              {formError ? <p style={{ margin: 0, fontSize: 12, color: ui.danger }}>{formError}</p> : null}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={ghostBtn} onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button type="button" style={primaryBtn} onClick={handleSave} disabled={isSaving}>
                {isSaving
                  ? 'Guardando...'
                  : form.status === 'Activa'
                    ? 'Guardar y activar'
                    : form.status === 'Borrador'
                      ? 'Guardar borrador'
                      : 'Guardar regla'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FinanceAccountingRulesPanel;
